import React, { useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Switch, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

const DOC_TYPES = [
  'Method_Statement',
  'ITP',
  'Drawing',
  'Permit_Attachment',
  'Inspection_Report',
  'Other',
];

type Props = { projectId?: string };

export const FieldDocumentsPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const packagesQ = useQuery({
    queryKey: ['cep-packages', projectId],
    queryFn: () => epcService.getExecutionPackages(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const docsQ = useQuery({
    queryKey: ['cep-field-docs', projectId],
    queryFn: () => epcService.getCepFieldDocuments(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createFieldDocument({ ...values, projectId }),
    onSuccess: () => {
      message.success('Field document linked');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-field-docs'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, isApprovedRevision }: { id: string; isApprovedRevision: boolean }) =>
      epcService.patchFieldDocument(id, { isApprovedRevision }),
    onSuccess: () => {
      message.success('Document updated');
      void qc.invalidateQueries({ queryKey: ['cep-field-docs'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for field documents</Tag>;

  const rows = (docsQ.data as any[]) || [];
  const packages = (packagesQ.data as any[]) || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          Link document
        </Button>
        <Tag color="geekblue">CEP Wave 4</Tag>
        <Tag>Prefer approved revisions only</Tag>
      </Space>
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={docsQ.isLoading}
        dataSource={rows}
        columns={[
          { title: 'Type', dataIndex: 'doc_type', width: 140 },
          { title: 'Title', dataIndex: 'title', ellipsis: true },
          { title: 'Ref', dataIndex: 'document_ref', width: 140 },
          { title: 'Rev', dataIndex: 'revision', width: 80 },
          {
            title: 'Approved',
            dataIndex: 'is_approved_revision',
            width: 110,
            render: (v: number, r: any) => (
              <Switch
                size="small"
                checked={!!v}
                onChange={(checked) =>
                  patchMut.mutate({ id: String(r.id), isApprovedRevision: checked })
                }
              />
            ),
          },
        ]}
      />
      <Modal
        title="Link field document"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
          initialValues={{ isApprovedRevision: true }}
        >
          <Form.Item name="packageId" label="Execution package" rules={[{ required: true }]}>
            <Select
              options={packages.map((p: any) => ({
                value: String(p.id),
                label: p.package_number,
              }))}
            />
          </Form.Item>
          <Form.Item name="docType" label="Document type" rules={[{ required: true }]}>
            <Select options={DOC_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="documentRef" label="Document reference">
            <Input />
          </Form.Item>
          <Form.Item name="revision" label="Revision">
            <Input />
          </Form.Item>
          <Form.Item name="isApprovedRevision" label="Approved revision" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FieldDocumentsPanel;
